import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import client from '../api/client';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import { Contenedor } from '../components/layout/Layout';

const Hero = styled.section`
  background: linear-gradient(160deg, #f4efe7 0%, #e9eee6 100%);
  padding: ${({ theme }) => theme.space.xxl} 0;
  text-align: center;
`;

const HeroContenido = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.space.md};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space.lg};
`;

const Eslogan = styled.p`
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.sage};
`;

const TituloHero = styled.h1`
  font-size: clamp(2.2rem, 6vw, 3.6rem);
  color: ${({ theme }) => theme.colors.sageDeep};
  font-weight: 600;
`;

const TextoHero = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textMuted};
  max-width: 52ch;
`;

const Seccion = styled.section`
  margin-top: ${({ theme }) => theme.space.xxl};
`;

const TituloSeccion = styled.h2`
  font-size: clamp(1.6rem, 3.5vw, 2.1rem);
  color: ${({ theme }) => theme.colors.sageDeep};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.space.xl};
`;

const Beneficios = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.space.lg};
  grid-template-columns: 1fr;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Beneficio = styled(Card)`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};

  h3 {
    font-size: 1.15rem;
  }

  p {
    font-size: 0.92rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Icono = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.sageLight};
  color: ${({ theme }) => theme.colors.sageDeep};
`;

const TiposGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.space.lg};
  grid-template-columns: 1fr;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const TipoCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};

  h3 {
    font-size: 1.15rem;
  }

  p {
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.textMuted};
    flex: 1;
  }
`;

const CierreCTA = styled.section`
  margin-top: ${({ theme }) => theme.space.xxl};
  background: ${({ theme }) => theme.colors.sageDeep};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: ${({ theme }) => theme.space.xxl} ${({ theme }) => theme.space.lg};
  text-align: center;
  color: #f2f0ea;

  h2 {
    font-size: clamp(1.6rem, 3.5vw, 2.2rem);
    margin-bottom: ${({ theme }) => theme.space.sm};
  }

  p {
    opacity: 0.85;
    margin-bottom: ${({ theme }) => theme.space.lg};
  }
`;

function IconoSVG({ trazo }) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {trazo}
    </svg>
  );
}

const beneficios = [
  {
    titulo: 'Grupos de verdad pequeños',
    texto: 'Nunca más de diez personas por clase: espacio real para corregir cada postura y ajustar cada ejercicio a tu cuerpo.',
    icono: (
      <>
        <circle cx="9" cy="7" r="3" />
        <circle cx="17" cy="9" r="2.5" />
        <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
        <path d="M15.5 14.5c2.5.5 4.5 2.8 4.5 5.5" />
      </>
    ),
  },
  {
    titulo: 'Instructores que te acompañan',
    texto: 'Certificaciones internacionales en mat, reformer, prenatal y terapéutico — y memoria de tu progreso clase a clase.',
    icono: (
      <>
        <circle cx="12" cy="9" r="5" />
        <path d="M9 13.5 7.5 21l4.5-2.5L16.5 21 15 13.5" />
      </>
    ),
  },
  {
    titulo: 'Reserva sin fricción',
    texto: 'Aparta tu lugar desde el celular en segundos, controla tu paquete y cancela hasta con 12 horas de anticipación.',
    icono: (
      <>
        <rect x="4" y="5" width="16" height="16" rx="2" />
        <path d="M8 3v4M16 3v4M4 11h16" />
        <path d="m9.5 15.5 2 2 3.5-3.5" />
      </>
    ),
  },
];

function Home() {
  const [tipos, setTipos] = useState([]);

  useEffect(() => {
    client.get('/tipos-clase').then(({ data }) => setTipos(data)).catch(() => {});
  }, []);

  return (
    <>
      <Hero>
        <HeroContenido>
          <Eslogan>Pilates boutique · Grupos pequeños</Eslogan>
          <TituloHero>Tu cuerpo, tu ritmo, tu clase</TituloHero>
          <TextoHero>
            Reformer, mat, prenatal y terapéutico en grupos de máximo diez personas. Un espacio
            donde el instructor conoce tu nombre, tu progreso y tus límites — no solo tu horario.
          </TextoHero>
          <Button as={Link} to="/agenda">
            Reserva tu clase
          </Button>
        </HeroContenido>
      </Hero>

      <Contenedor>
        <Seccion as="div">
          <TituloSeccion>Lo que nos hace diferentes</TituloSeccion>
          <Beneficios>
            {beneficios.map(({ titulo, texto, icono }) => (
              <Beneficio key={titulo}>
                <Icono>
                  <IconoSVG trazo={icono} />
                </Icono>
                <h3>{titulo}</h3>
                <p>{texto}</p>
              </Beneficio>
            ))}
          </Beneficios>
        </Seccion>

        {tipos.length > 0 && (
          <Seccion>
            <TituloSeccion>Nuestras clases</TituloSeccion>
            <TiposGrid>
              {tipos.map((tipo) => (
                <TipoCard key={tipo.id}>
                  <h3>{tipo.nombre}</h3>
                  {tipo.nivel && <Badge $tono="arena">{tipo.nivel}</Badge>}
                  <p>{tipo.descripcion}</p>
                  <Button as={Link} to="/agenda" $variante="secundario" $chico>
                    Ver horarios
                  </Button>
                </TipoCard>
              ))}
            </TiposGrid>
          </Seccion>
        )}

        <CierreCTA>
          <h2>Ven a conocer el estudio</h2>
          <p>Prueba una clase suelta o entra directo con el paquete que va con tu ritmo semanal.</p>
          <Button as={Link} to="/paquetes" $variante="secundario" style={{ background: '#f2f0ea' }}>
            Ver paquetes y precios
          </Button>
        </CierreCTA>
      </Contenedor>
    </>
  );
}

export default Home;
